/* global BigInt */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './App.css';

function App() {
  // State from previous version
  const [endpoint, setEndpoint] = useState('/api/v1/websocket-message');
  const [baseDeviceId, setBaseDeviceId] = useState('867395078103001');
  const [deviceCount, setDeviceCount] = useState(20);
  const [delay, setDelay] = useState(15000);
  const [isRunning, setIsRunning] = useState(false);
  const [logs, setLogs] = useState([]);

  // Refs for data (unchanged)
  const dataRef = useRef([]);
  const intervalsRef = useRef({});
  const currentIndexesRef = useRef({});

  // Load data.json (unchanged)
  useEffect(() => {
    fetch('/data.json')
      .then(response => response.json())
      .then(data => {
        dataRef.current = data;
      })
      .catch(error => {
        console.error('Error loading data.json:', error);
        setLogs(prev => [...prev, {
          timestamp: new Date().toISOString(),
          message: `Error loading data.json: ${error.message}`,
          status: 'error'
        }]);
      });
  }, []);

  // Send data to endpoint (unchanged)
  const sendData = async (deviceId, data, index) => {
    try {
      const message = {
        action: "80003",
        payload: {
          deviceID: deviceId,
          nodeID: "7E22591A-12A74FB2-9B3A1817-58D779FB",
          dtu: new Date().toISOString().slice(0, 19).replace('T', ' '),
          location: {
            mode: "1",
            dtu: new Date().toISOString().slice(0, 19).replace('T', ' '),
            direct: Math.floor(Math.random() * 360).toString(),
            satellites: data.satellites?.toString() || "19",
            speed: data.speed?.toString() || "0",
            altitude: data.altitude?.toString() || "0",
            precision: "0",
            longitude: data.longitude?.toString() || "0",
            latitude: data.latitude?.toString() || "0"
          },
          gsensor: { x: "0.08", y: "0.12", z: "0.98", tilt: "17.48", hit: "0.99" },
          basic: { key: "1", left: "1", right: "1" },
          module: { mobile: "1", location: "1", wifi: "0", gsensor: "1", record: "0x1f" },
          fuel: { cost: "0.00" },
          mobile: { type: "5", strength: "10" },
          wifi: { strength: "0", ssid: "" },
          storage: [{ name: "sd1", index: "0", status: "1", total: "118900", free: "0" }],
          alarm: { videoLost: "0xe0", motionDection: "0x0", videoMask: "0x0", input: "0x0", overSpeed: "0x0", lowSpeed: "0x0", urgency: "0x0" },
          mileage: { todayDay: "100560", total: "6627110" },
          voltage: { vcc: "27.50", bat: "12.10", vo1: "0.052" },
          driver: { id: "", name: "" },
          bluetooh: { connect: "0" },
          load: { status: "0" },
          devtemp: { cpu: "69.0", disk: "-0.0" },
          ext: { vol1: 0.052, isLater: false, reportTime: Date.now() }
        }
      };

      const response = await axios.post(endpoint, message);

      setLogs(prev => [...prev, {
        timestamp: new Date().toISOString(),
        message: `Sent data for device ${deviceId} (index: ${index}) - Lat: ${data.latitude}, Lng: ${data.longitude}`,
        status: 'success'
      }]);

      return response;
    } catch (error) {
      setLogs(prev => [...prev, {
        timestamp: new Date().toISOString(),
        message: `Failed to send data for device ${deviceId}: ${error.message}`,
        status: 'error'
      }]);
      throw error;
    }
  };

  // Simulation logic (unchanged)
  const startSimulation = () => {
    if (!baseDeviceId || deviceCount <= 0) {
      setLogs(prev => [...prev, {
        timestamp: new Date().toISOString(),
        message: 'Please provide a valid Base Device ID and a data count greater than 0.',
        status: 'error'
      }]);
      return;
    }

    const devicesToSimulate = [];
    const baseIdLength = baseDeviceId.length;
    const baseIdNum = BigInt(baseDeviceId);

    for (let i = 0; i < deviceCount; i++) {
      const newId = (baseIdNum + BigInt(i)).toString();

      if (newId.length > baseIdLength) {
        setLogs(prev => [...prev, {
          timestamp: new Date().toISOString(),
          message: `Error: Incrementing Base ID by ${i} changes its character length. Please use a smaller Base ID number (e.g., ...001).`,
          status: 'error'
        }]);
        return;
      }
      devicesToSimulate.push({ id: newId });
    }

    setIsRunning(true);
    setLogs([{
      timestamp: new Date().toISOString(),
      message: `Simulation started for ${deviceCount} devices.`,
      status: 'info'
    }]);

    Object.values(intervalsRef.current).forEach(clearInterval);
    intervalsRef.current = {};

    devicesToSimulate.forEach((_, index) => {
      currentIndexesRef.current[index] = index * 10;
    });

    devicesToSimulate.forEach((device, deviceIndex) => {
      if (device.id) {
        intervalsRef.current[deviceIndex] = setInterval(() => {
          if (dataRef.current.length > 0) {
            const currentIndex = currentIndexesRef.current[deviceIndex] || (deviceIndex * 10);
            const dataPoint = dataRef.current[currentIndex % dataRef.current.length];
            sendData(device.id, dataPoint, currentIndex);
            currentIndexesRef.current[deviceIndex] = currentIndex + 1;
          }
        }, delay);
      }
    });
  };

  const stopSimulation = () => {
    setIsRunning(false);
    Object.values(intervalsRef.current).forEach(clearInterval);
    intervalsRef.current = {};
    setLogs(prev => [...prev, {
      timestamp: new Date().toISOString(),
      message: 'Simulation stopped',
      status: 'info'
    }]);
  };

  // --- FUNGSI BARU UNTUK MEMBERSIHKAN LOG ---
  const clearLogs = () => {
    setLogs([]);
  };

  // Clean up intervals on unmount (unchanged)
  useEffect(() => {
    return () => {
      Object.values(intervalsRef.current).forEach(clearInterval);
    };
  }, []);

  return (
    <div className="App">
      <header className="App-header">
        <h1>McEasy VSS WebSocket Simulator</h1>
      </header>

      <main className="App-main">
        <div className="config-section">
          <h2>Configuration</h2>

          <div className="config-item">
            <label htmlFor="endpoint">Endpoint Path (Proxied):</label>
            <input
              type="text"
              id="endpoint"
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              disabled={isRunning}
            />
          </div>

          <div className="config-item">
            <label htmlFor="baseDeviceId">Base Device ID:</label>
            <input
              type="text"
              id="baseDeviceId"
              value={baseDeviceId}
              onChange={(e) => setBaseDeviceId(e.target.value)}
              disabled={isRunning}
              placeholder="e.g., 867395078103001"
            />
          </div>

          <div className="config-item">
            <label htmlFor="deviceCount">Number of Data/Devices:</label>
            <input
              type="number"
              id="deviceCount"
              value={deviceCount}
              onChange={(e) => setDeviceCount(parseInt(e.target.value, 10))}
              disabled={isRunning}
              placeholder="e.g., 20"
            />
          </div>

          <div className="config-item">
            <label htmlFor="delay">Delay (ms):</label>
            <input
              type="number"
              id="delay"
              value={delay}
              onChange={(e) => setDelay(parseInt(e.target.value, 10))}
              disabled={isRunning}
              placeholder="e.g., 15000"
            />
          </div>

          <div className="controls">
            {!isRunning ? (
              <button onClick={startSimulation}>Start Simulation</button>
            ) : (
              <button onClick={stopSimulation}>Stop Simulation</button>
            )}
          </div>
        </div>

        <div className="status-section">
          <h2>Status</h2>
          <div className="status-info">
            <p>Simulation: {isRunning ? 'Running' : 'Stopped'}</p>
            {isRunning && <p>Simulating {deviceCount} devices...</p>}
          </div>
        </div>

        <div className="logs-section">
          {/* --- TOMBOL CLEAR LOGS DITAMBAHKAN DI SINI --- */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h2>Logs</h2>
            <button
              onClick={clearLogs}
              style={{
                padding: '8px 12px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Clear Logs
            </button>
          </div>
          <div className="logs-container">
            {logs.map((log, index) => (
              <div key={index} className={`log-entry log-${log.status}`}>
                <span className="log-timestamp">[{new Date(log.timestamp).toLocaleTimeString()}]</span>
                <span className="log-message">{log.message}</span>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}

export default App;

